import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

export interface StaleCardDialogData {
  /** Relevance reason code, e.g. RENT_ALREADY_PAID. */
  reasonCode: string;
  /** Optional detail (a date / year) interpolated into the message. */
  detail?: string;
}

export type StaleCardDialogResult = 'dismiss' | 'force';

/**
 * Warning shown when the owner approves a card whose action is no longer
 * relevant (rent already paid, indexation applied, lease renewed). Lets the
 * owner file the card away or send anyway — never silently sends.
 */
@Component({
  selector: 'app-stale-card-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title class="flex items-center gap-2">
      <mat-icon class="text-amber-500">info</mat-icon>
      {{ 'cockpit.relevance.dialog.title' | translate }}
    </h2>
    <mat-dialog-content>
      <p class="text-sm text-slate-700 dark:text-slate-200" data-testid="stale-reason">
        {{ reasonKey | translate: { detail: data.detail } }}
      </p>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
        {{ 'cockpit.relevance.dialog.subtitle' | translate }}
      </p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close type="button">
        {{ 'cockpit.relevance.dialog.cancel' | translate }}
      </button>
      <button
        mat-stroked-button
        color="warn"
        type="button"
        (click)="close('force')"
        data-testid="stale-force">
        {{ 'cockpit.relevance.dialog.sendAnyway' | translate }}
      </button>
      <button
        mat-flat-button
        color="primary"
        type="button"
        (click)="close('dismiss')"
        data-testid="stale-dismiss">
        <mat-icon class="!w-4 !h-4 !text-[16px] mr-1 leading-none">archive</mat-icon>
        {{ 'cockpit.relevance.dialog.dismiss' | translate }}
      </button>
    </mat-dialog-actions>
  `,
})
export class StaleCardDialogComponent {
  private readonly ref =
    inject(MatDialogRef<StaleCardDialogComponent, StaleCardDialogResult>);
  readonly data = inject<StaleCardDialogData>(MAT_DIALOG_DATA);

  /** Per-reason message key, with a generic fallback for unknown codes. */
  get reasonKey(): string {
    const known = new Set([
      'RENT_ALREADY_PAID',
      'RENT_PERIOD_SKIPPED',
      'INDEXATION_ALREADY_APPLIED',
      'LEASE_ALREADY_RENEWED',
      'CONTRACT_TERMINATED',
      'CONTRACT_NOT_ACTIVE',
    ]);
    return known.has(this.data.reasonCode)
      ? `cockpit.relevance.reason.${this.data.reasonCode}`
      : 'cockpit.relevance.reason.generic';
  }

  protected close(result: StaleCardDialogResult): void {
    this.ref.close(result);
  }
}
